import * as React from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  Boxes,
  ClipboardList,
  Database,
  FolderKanban,
  HelpCircle,
  History,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
  Sparkles,
  Target,
  Users,
  Building2,
  CalendarClock,
  Menu,
  Palette,
  Timer,
  Wallet,
  X,
} from "lucide-react";
import { roleHasPermission, type AppRole, type FeatureFlags, type FeatureKey, type Permission } from "@order-analyzer/shared";
import { LoadingState, Switch } from "@order-analyzer/ui";
import { useAuth } from "./auth.js";
import { BlockedUserScreen } from "./pages/BlockedUserScreen.js";
import { api, setTokenProvider } from "./api.js";
import { applyTheme, getStoredTheme, type Theme } from "./theme.js";
import { NotificationBell } from "./components/NotificationBell.js";
import { RequirePermission } from "./components/RequirePermission.js";
import { RequireFeature, useFeatureFlags } from "./lib/features.js";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary.js";
import { FeedbackDialog } from "./components/FeedbackDialog.js";
import { ReleaseNotesDialog } from "./components/ReleaseNotesDialog.js";
import { WhatsNewDialog } from "./components/WhatsNewDialog.js";
import { useBranding } from "./brandingContext.js";

// Jede Seite wird als eigener Chunk nachgeladen statt in einem einzigen, über 1,7 MB großen
// Startbundle enthalten zu sein - Nutzer laden beim ersten Aufruf nur die Seiten, die sie
// tatsächlich besuchen (z.B. braucht ein reiner Business Manager nie den Code für KI-Zuordnung
// oder Import und Export). <Suspense> unten zeigt während des Nachladens einen kurzen Ladehinweis.
const DashboardPage = React.lazy(() => import("./pages/DashboardPage.js").then((m) => ({ default: m.DashboardPage })));
const BusinessManagerDashboardPage = React.lazy(() =>
  import("./pages/BusinessManagerDashboardPage.js").then((m) => ({ default: m.BusinessManagerDashboardPage })),
);
const BmReportsPage = React.lazy(() => import("./pages/BmReportsPage.js").then((m) => ({ default: m.BmReportsPage })));
const BmReportEditorPage = React.lazy(() => import("./pages/BmReportEditorPage.js").then((m) => ({ default: m.BmReportEditorPage })));
const BmReportConsolidatedPage = React.lazy(() =>
  import("./pages/BmReportConsolidatedPage.js").then((m) => ({ default: m.BmReportConsolidatedPage })),
);
const BmTopicResponsibilitiesPage = React.lazy(() =>
  import("./pages/BmTopicResponsibilitiesPage.js").then((m) => ({ default: m.BmTopicResponsibilitiesPage })),
);
const ProjectsPage = React.lazy(() => import("./pages/ProjectsPage.js").then((m) => ({ default: m.ProjectsPage })));
const ProjectDetailPage = React.lazy(() => import("./pages/ProjectDetailPage.js").then((m) => ({ default: m.ProjectDetailPage })));
const ActivitiesPage = React.lazy(() => import("./pages/ActivitiesPage.js").then((m) => ({ default: m.ActivitiesPage })));
const TopicsPage = React.lazy(() => import("./pages/TopicsPage.js").then((m) => ({ default: m.TopicsPage })));
const PeoplePage = React.lazy(() => import("./pages/PeoplePage.js").then((m) => ({ default: m.PeoplePage })));
const PersonDetailPage = React.lazy(() => import("./pages/PersonDetailPage.js").then((m) => ({ default: m.PersonDetailPage })));
const CustomersPage = React.lazy(() => import("./pages/CustomersPage.js").then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = React.lazy(() => import("./pages/CustomerDetailPage.js").then((m) => ({ default: m.CustomerDetailPage })));
const AiAssignmentPage = React.lazy(() => import("./pages/AiAssignmentPage.js").then((m) => ({ default: m.AiAssignmentPage })));
const AiJobDetailPage = React.lazy(() => import("./pages/AiJobDetailPage.js").then((m) => ({ default: m.AiJobDetailPage })));
const AiPersonJobDetailPage = React.lazy(() => import("./pages/AiPersonJobDetailPage.js").then((m) => ({ default: m.AiPersonJobDetailPage })));
const PlanningPage = React.lazy(() => import("./pages/PlanningPage.js").then((m) => ({ default: m.PlanningPage })));
const DataQualityPage = React.lazy(() => import("./pages/DataQualityPage.js").then((m) => ({ default: m.DataQualityPage })));
const ImportExportPage = React.lazy(() => import("./pages/ImportExportPage.js").then((m) => ({ default: m.ImportExportPage })));
const AdminPage = React.lazy(() => import("./pages/AdminPage.js").then((m) => ({ default: m.AdminPage })));
const AuditLogPage = React.lazy(() => import("./pages/AuditLogPage.js").then((m) => ({ default: m.AuditLogPage })));
const FeedbackPage = React.lazy(() => import("./pages/FeedbackPage.js").then((m) => ({ default: m.FeedbackPage })));
const SystemStatusPage = React.lazy(() => import("./pages/SystemStatusPage.js").then((m) => ({ default: m.SystemStatusPage })));
const AdminBrandingPage = React.lazy(() => import("./pages/AdminBrandingPage.js").then((m) => ({ default: m.AdminBrandingPage })));
const ActivityTimelinePage = React.lazy(() => import("./pages/ActivityTimelinePage.js"));
const UserJiraConnectionsPage = React.lazy(() => import("./pages/UserJiraConnectionsPage.js"));
const HelpPage = React.lazy(() => import("./pages/HelpPage.js").then((m) => ({ default: m.HelpPage })));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage.js").then((m) => ({ default: m.NotFoundPage })));

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  anyPermission?: Permission[];
  // Nur für die Rolle "Administrator" sichtbar - unabhängig davon, ob andere Rollen die
  // zugrunde liegende Berechtigung ebenfalls haben. Die Route ist zusätzlich per
  // <RequirePermission role="Administrator"> geschützt (siehe Routes unten).
  adminOnly?: boolean;
  // Optionales Modul (Administration -> Funktionen) - ausgeblendet, solange es deaktiviert ist.
  feature?: FeatureKey;
};

// Die Navigation ist in fachliche Bereiche gegliedert. Bereiche, in denen für die aktuelle Rolle
// kein einziger Link sichtbar ist, werden samt Überschrift ausgeblendet (siehe Shell).
const navSections: Array<{ title?: string; links: NavItem[] }> = [
  {
    links: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    // Projekte/Themen/Personen/Kunden sind bewusst rollenübergreifend lesbar (allgemeine
    // Unternehmens-Referenzdaten ohne eigene granulare Leseberechtigung) - jede Rolle benötigt
    // zumindest Kontext aus diesen Listen (z.B. sieht ein Business Manager beim Aufruf eines
    // Projekts auch dessen bereits gesetzte Themenzuordnung). Schreibende Aktionen bleiben über
    // eigene Berechtigungen (topics:write, assignments:write, data:delete, ...) serverseitig
    // gesperrt.
    title: "Stammdaten",
    links: [
      { to: "/projekte", label: "Projekte", icon: FolderKanban },
      { to: "/themen", label: "Themen", icon: Boxes },
      { to: "/personen", label: "Personen", icon: Users },
      { to: "/kunden", label: "Kunden", icon: Building2 },
    ],
  },
  {
    title: "Analyse und Planung",
    links: [
      // Nur für Rollen sichtbar, die überhaupt eine KI-Klassifizierung anstoßen (ai:run) oder deren
      // Vorschläge prüfen/übernehmen können (assignments:write) - für alle anderen Rollen wäre
      // die Seite funktionslos.
      { to: "/ki-zuordnung", label: "KI-Zuordnung", icon: Sparkles, anyPermission: ["ai:run", "assignments:write"] },
      { to: "/planung", label: "Planung 2027", icon: Target, anyPermission: ["planning:read", "planning:write"], feature: "planning" },
    ],
  },
  {
    title: "Business Manager",
    links: [
      // Business-Manager-Umsatzverteilung ist bewusst von der Themen-/KI-Zuordnung getrennt (eigene
      // Berechtigungen "bm:read"/"bm:read-all") und daher nur für Business Manager, Administratoren
      // und Business Executives sichtbar.
      { to: "/business-manager", label: "Umsatzverteilung", icon: Wallet, anyPermission: ["bm:read", "bm:read-all"] },
      // Der zweiwöchentliche Statusbericht ist bewusst von der Umsatzverteilung getrennt. "Meine
      // Berichte" zeigt die eigenen Perioden und setzt daher bm-report:write voraus - Geschäftsführung
      // und Business Executive haben keine eigenen Berichte, sie lesen über die konsolidierte Sicht.
      { to: "/bm-berichte", label: "Meine Berichte", icon: CalendarClock, anyPermission: ["bm-report:write"], feature: "bmReports" },
      { to: "/bm-berichte/konsolidiert", label: "Berichte der BM", icon: ClipboardList, anyPermission: ["bm-report:read-all"], feature: "bmReports" },
      // Grundlage der zweiten Auftragseingangs-Sicht im Bericht; gepflegt von Administration und
      // Business Executive (bm-report:write-topics), nicht vom Business Manager selbst.
      { to: "/themenverantwortung", label: "Themenverantwortung", icon: Boxes, anyPermission: ["bm-report:write-topics"], feature: "bmReports" },
    ],
  },
  {
    // Rohdaten und deren Pflege - ausschließlich für Administratoren. Die KI-Analyse liest
    // Zeiteinträge dennoch immer vollständig (direkt per Prisma, unabhängig von der Sichtbarkeit
    // der Seite "Tätigkeiten"), siehe timeEntries.ts.
    title: "Daten",
    links: [
      { to: "/tätigkeiten", label: "Tätigkeiten", icon: ClipboardList, adminOnly: true },
      { to: "/datenqualität", label: "Datenqualität", icon: BarChart3, adminOnly: true },
      { to: "/import-export", label: "Import und Export", icon: Database, adminOnly: true },
    ],
  },
  {
    title: "Verwaltung",
    links: [
      // Administration bündelt mehrere eigenständig berechtigte Bereiche (KI-Konfiguration/Backup:
      // config:write, Benutzer und Rollen: users:write, persönliches Umsatzziel: bm-target:write) -
      // sichtbar für jede Rolle, die mindestens eine davon hat; AdminPage.tsx blendet die jeweils
      // nicht zutreffenden Karten zusätzlich einzeln aus.
      { to: "/administration", label: "Administration", icon: Settings, end: true, anyPermission: ["users:write", "config:write", "bm-target:write"] },
      { to: "/administration/audit-log", label: "Audit-Log", icon: History, adminOnly: true },
      // Feedback-Übersicht (eingereicht über den Feedback-Button unten links, siehe Shell) ist
      // ausschließlich für Administratoren sichtbar (feedback:read). Der Badge-Zähler für offenes
      // Feedback wird in Shell separat abgefragt und über badgeCount an NavigationLink durchgereicht.
      { to: "/administration/feedback", label: "Feedback", icon: MessageSquare, anyPermission: ["feedback:read"] },
      // Betriebssicht (Datenbank, AppHub-Anbindung, E-Mail-Selbsttest, unerwartete Fehler) - dieselbe
      // Berechtigung wie Datensicherung/KI-Konfiguration in der Administration (config:write).
      { to: "/administration/systemstatus", label: "Systemstatus", icon: Activity, anyPermission: ["config:write"] },
      { to: "/administration/branding", label: "Branding", icon: Palette, anyPermission: ["config:write"] },
      { to: "/administration/activity-timeline", label: "ActivityTimeline-Sync", icon: Timer, anyPermission: ["activity-timeline:manage"], feature: "activityTimeline" },
    ],
  },
  {
    title: "Persönlich",
    links: [
      // Sichtbar fuer JEDEN angemeldeten Benutzer - persoenliche Zugangsdaten, keine Admin-Funktion.
      { to: "/mein-profil/jira-verbindungen", label: "Meine Jira-Verbindungen", icon: KeyRound, feature: "jiraConnections" },
      // Hilfebereich: für jeden angemeldeten Benutzer sichtbar, Inhalte werden rollenbasiert gefiltert
      // (siehe HelpPage.tsx/helpContent.ts) - daher hier bewusst kein anyPermission-Filter.
      { to: "/hilfe", label: "Hilfe", icon: HelpCircle },
    ],
  },
];

function isNavItemVisible(link: NavItem, roles: string[], features: FeatureFlags): boolean {
  if (link.feature && !features[link.feature]) return false;
  if (link.adminOnly && !roles.includes("Administrator")) return false;
  if (!link.anyPermission) return true;
  return roles.some((role) => link.anyPermission!.some((p) => roleHasPermission(role as AppRole, p)));
}

function NavigationLink({ to, label, icon: Icon, end, badgeCount }: NavItem & { badgeCount?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-brand/10 text-brand dark:bg-brand/20"
            : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1">{label}</span>
      {Boolean(badgeCount) && (
        <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
          {badgeCount! > 9 ? "9+" : badgeCount}
        </span>
      )}
    </NavLink>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { user, isMsalEnabled, logout } = useAuth();
  const featureFlags = useFeatureFlags();
  const branding = useBranding();
  const [theme, setTheme] = React.useState<Theme>(getStoredTheme());
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = React.useState(false);

  // Verhindert Hintergrund-Scrollen, während die mobile Navigation als Overlay über dem Inhalt
  // liegt (sonst lässt sich hinter der Navigation "durchscrollen", was auf einem Touchscreen
  // schnell passiert). Auf Desktop-Breiten bleibt mobileNavOpen ohnehin immer false, da der
  // Hamburger-Button dort per lg:hidden gar nicht sichtbar ist.
  React.useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileNavOpen]);
  // Liest die tatsächlich laufende Backend-Version aus /api/health (package.json), statt eine
  // im Frontend fest hinterlegte Versionsnummer zu pflegen, die bei jedem Release veraltet wäre.
  const versionQuery = useQuery({
    queryKey: ["health-version"],
    queryFn: () => api.get<{ version: string }>("/health"),
    staleTime: 5 * 60 * 1000,
  });

  const canReadFeedback = (user?.roles ?? []).some((role) => roleHasPermission(role as AppRole, "feedback:read"));
  const feedbackOpenCountQuery = useQuery({
    queryKey: ["feedback", "open-count"],
    queryFn: () => api.get<{ count: number }>("/feedback/open-count"),
    enabled: canReadFeedback,
    refetchInterval: 30000,
  });

  const toggleTheme = (dark: boolean) => {
    const next: Theme = dark ? "dark" : "light";
    setTheme(next);
    applyTheme(next);
  };

  return (
    <div className="mx-auto grid min-h-screen max-w-[1600px] grid-cols-1 lg:h-screen lg:grid-cols-[250px_minmax(0,1fr)] lg:overflow-hidden">
      {mobileNavOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[85vw] max-w-[300px] transform overflow-y-auto border-r border-slate-200 bg-white shadow-xl transition-transform duration-200 ease-in-out dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-auto lg:max-w-none lg:translate-x-0 lg:bg-white/70 lg:shadow-none lg:backdrop-blur lg:dark:bg-slate-900/70 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col gap-4 p-4">
          <div className="flex items-center gap-2 px-2 py-2">
            <img src={branding.logoDataUrl ?? "/favicon.svg"} alt="" className="h-7 w-7" />
            <span className="text-base font-semibold text-ink dark:text-slate-50">{branding.appName}</span>
            <button
              onClick={() => setMobileNavOpen(false)}
              aria-label="Navigation schließen"
              className="ml-auto rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 lg:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col gap-4" aria-label="Hauptnavigation" onClick={() => setMobileNavOpen(false)}>
            {navSections.map((section, index) => {
              const visibleLinks = section.links.filter((link) => isNavItemVisible(link, user?.roles ?? [], featureFlags));
              if (visibleLinks.length === 0) return null;
              const headingId = section.title ? `nav-section-${index}` : undefined;
              return (
                <div key={section.title ?? index} role="group" aria-labelledby={headingId} className="flex flex-col gap-1">
                  {section.title && (
                    <p
                      id={headingId}
                      className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500"
                    >
                      {section.title}
                    </p>
                  )}
                  {visibleLinks.map((link) => (
                    <NavigationLink
                      key={link.to}
                      {...link}
                      badgeCount={link.to === "/administration/feedback" ? feedbackOpenCountQuery.data?.count : undefined}
                    />
                  ))}
                </div>
              );
            })}
          </nav>
          <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              onClick={() => setFeedbackDialogOpen(true)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <MessageSquare className="h-3.5 w-3.5" /> Feedback geben
            </button>
            <div className="flex items-center justify-between px-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Dunkler Modus</span>
              <Switch checked={theme === "dark"} onCheckedChange={toggleTheme} label="Dunkler Modus umschalten" />
            </div>
            <div className="px-2 text-xs text-slate-500 dark:text-slate-400">
              <p className="font-medium text-ink dark:text-slate-100">{user?.displayName ?? "Unbekannt"}</p>
              <p>{user?.roles?.join(", ") ?? ""}</p>
            </div>
            {isMsalEnabled && (
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <LogOut className="h-3.5 w-3.5" /> Abmelden
              </button>
            )}
            <button
              type="button"
              onClick={() => setReleaseNotesOpen(true)}
              disabled={!versionQuery.data}
              className="px-2 text-left text-[11px] text-slate-400 hover:text-slate-600 hover:underline disabled:no-underline dark:text-slate-600 dark:hover:text-slate-400"
            >
              {versionQuery.data ? `Version ${versionQuery.data.version}` : "Version wird geladen ..."}
            </button>
          </div>
        </div>
      </aside>
      <div className="flex flex-col lg:h-screen lg:overflow-hidden">
        <header className="shrink-0 flex items-center gap-3 border-b border-slate-200 bg-white/70 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/70">
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label="Navigation umschalten"
            className="-ml-2 rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-semibold text-ink dark:text-slate-50 lg:hidden">{branding.appName}</span>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:overflow-y-auto">{children}</main>
      </div>
      <FeedbackDialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} />
      <ReleaseNotesDialog open={releaseNotesOpen} onClose={() => setReleaseNotesOpen(false)} />
      <WhatsNewDialog />
    </div>
  );
}

function LoginGate() {
  const { login } = useAuth();
  const branding = useBranding();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <img src={branding.logoDataUrl ?? "/favicon.svg"} alt="" className="h-12 w-12" />
      <h1 className="text-lg font-semibold text-ink dark:text-slate-50">{branding.appName}</h1>
      {branding.loginDescription && <p className="max-w-sm text-center text-sm text-slate-500 dark:text-slate-400">{branding.loginDescription}</p>}
      <p className="text-sm text-slate-500 dark:text-slate-400">Bitte melden Sie sich mit Ihrem Microsoft-Entra-ID-Konto an.</p>
      <button onClick={login} className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90">
        Anmelden
      </button>
    </div>
  );
}

export function App() {
  const { user, loading, isMsalEnabled, getAccessToken } = useAuth();
  const branding = useBranding();

  React.useEffect(() => {
    setTokenProvider(getAccessToken);
  }, [getAccessToken]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">{branding.appName} wird geladen ...</div>;
  }
  if (isMsalEnabled && !user) {
    return <LoginGate />;
  }
  // Gesperrter Nutzer: alle App-Routen und Datenaufrufe wuerden ohnehin mit 403 USER_BLOCKED
  // ablaufen (siehe requireAuth in apps/api/src/auth.ts). Statt einer Sammlung von Fehlerbannern
  // rendert das Frontend die dedizierte Sperrseite, die den in der Administration konfigurierten
  // Markdown-Text zeigt und Links unterstuetzt.
  if (user?.blockedAt) {
    return <BlockedUserScreen />;
  }

  return (
    <Shell>
      <ChunkErrorBoundary>
      <React.Suspense fallback={<LoadingState label="Seite wird geladen ..." />}>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/projekte" element={<ProjectsPage />} />
        <Route path="/projekte/:id" element={<ProjectDetailPage />} />
        <Route
          path="/tätigkeiten"
          element={
            <RequirePermission anyPermission={["activities:read"]} role="Administrator">
              <ActivitiesPage />
            </RequirePermission>
          }
        />
        <Route path="/themen" element={<TopicsPage />} />
        <Route path="/personen" element={<PeoplePage />} />
        <Route path="/personen/:id" element={<PersonDetailPage />} />
        <Route path="/kunden" element={<CustomersPage />} />
        <Route path="/kunden/:id" element={<CustomerDetailPage />} />
        <Route
          path="/ki-zuordnung"
          element={
            <RequirePermission anyPermission={["ai:run", "assignments:write"]}>
              <AiAssignmentPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ki-zuordnung/:jobId"
          element={
            <RequirePermission anyPermission={["ai:run", "assignments:write"]}>
              <AiJobDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ki-zuordnung/personen/:jobId"
          element={
            <RequirePermission anyPermission={["ai:run", "assignments:write"]}>
              <AiPersonJobDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/planung"
          element={
            <RequirePermission anyPermission={["planning:read", "planning:write"]}>
              <RequireFeature feature="planning">
                <PlanningPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/business-manager"
          element={
            <RequirePermission anyPermission={["bm:read", "bm:read-all"]}>
              <BusinessManagerDashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/bm-berichte"
          element={
            <RequirePermission anyPermission={["bm-report:write"]}>
              <RequireFeature feature="bmReports">
                <BmReportsPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        {/* Statische Route vor der dynamischen, damit "konsolidiert" nicht als Bericht-ID gelesen wird. */}
        <Route
          path="/bm-berichte/konsolidiert"
          element={
            <RequirePermission anyPermission={["bm-report:read-all"]}>
              <RequireFeature feature="bmReports">
                <BmReportConsolidatedPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/bm-berichte/:id"
          element={
            <RequirePermission anyPermission={["bm-report:write", "bm-report:read-all"]}>
              <RequireFeature feature="bmReports">
                <BmReportEditorPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/themenverantwortung"
          element={
            <RequirePermission anyPermission={["bm-report:write-topics"]}>
              <RequireFeature feature="bmReports">
                <BmTopicResponsibilitiesPage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/datenqualität"
          element={
            <RequirePermission role="Administrator">
              <DataQualityPage />
            </RequirePermission>
          }
        />
        <Route
          path="/import-export"
          element={
            <RequirePermission anyPermission={["import:write", "export:read"]} role="Administrator">
              <ImportExportPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration"
          element={
            <RequirePermission anyPermission={["users:write", "config:write", "bm-target:write"]}>
              <AdminPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/audit-log"
          element={
            <RequirePermission anyPermission={["audit:read"]}>
              <AuditLogPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/feedback"
          element={
            <RequirePermission anyPermission={["feedback:read"]}>
              <FeedbackPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/systemstatus"
          element={
            <RequirePermission anyPermission={["config:write"]}>
              <SystemStatusPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/branding"
          element={
            <RequirePermission anyPermission={["config:write"]}>
              <AdminBrandingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/activity-timeline"
          element={
            <RequirePermission anyPermission={["activity-timeline:manage"]}>
              <RequireFeature feature="activityTimeline">
                <ActivityTimelinePage />
              </RequireFeature>
            </RequirePermission>
          }
        />
        <Route
          path="/mein-profil/jira-verbindungen"
          element={
            <RequireFeature feature="jiraConnections">
              <UserJiraConnectionsPage />
            </RequireFeature>
          }
        />
        <Route path="/hilfe" element={<HelpPage />} />
        {/* Echte 404s (Tippfehler, alte Lesezeichen) zeigten bislang eine leere Seite statt einer
            Rückmeldung - App-Konsistenz-Audit gegen TranscriptWebapp, Thema 8. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </React.Suspense>
      </ChunkErrorBoundary>
    </Shell>
  );
}
